import { headreachFeel } from './boat/headreach.ts';
import { applyPlaning, planingTurnSpeed } from './boat/planing.ts';
import { reachIsDisk, type ReachAxes } from './boat/reach.ts';
import { DRIVE_SCALE, driveLevel } from './crashCost.ts';
import type { WindFeel } from './environment/wind.ts';
import type { MapDocument } from './map/types.ts';
import { solveTurnArc } from './movement.ts';
import { planMove, type CommitResult, type FleetContext } from './turn.ts';
import type { BoatState, Vec2 } from './types.ts';

/** Which ring a race was sailed with. Omitted means the authored ring. */
export type RingMode = 'off' | 'planing' | 'headreach';

export const RING_MODES = ['off', 'planing', 'headreach'] as const;

export function nextRingMode(mode: RingMode): RingMode {
  const index = RING_MODES.indexOf(mode);

  return RING_MODES[(index + 1) % RING_MODES.length] ?? 'off';
}

/**
 * Everything a seat's click needs besides the boat and the map. Live play, replay
 * reconstruction, and bots all build their `planMove` arguments from this one shape so the
 * ring, planing, and headreach projection cannot drift between them.
 */
export type TurnFeel = {
  /** Effective movement radius; the authored disk when `reach` is null. */
  radius: number;
  /** Authored hull axes. Null is the disk of `radius`. */
  reach: ReachAxes | null;
  ringMode: RingMode;
  planingSpeedKmh: number;
  bowOffsetPx: number;
  crashHarshness: number;
  turnTax: number;
  windFeel: WindFeel;
};

export type RingProjected = { level: number; coastPx: number; axes: ReachAxes };

export type RingProjection = {
  projected: RingProjected;
  /** Live oval, or null when the projected axes are a disk. */
  reach: ReachAxes | null;
  diskRadius: number;
};

/** Positional arguments `planMove`, `tryCommit`, and `commitActive` take after the destination. */
export type PlanMoveArgs = {
  radius: number;
  crashHarshness: number;
  turnTax: number;
  windFeel: WindFeel;
  reach: ReachAxes | null;
  bowOffsetPx: number;
  coastPx: number;
  keptSpeed: number | undefined;
};

export function ringProjection(speedPx: number, feel: TurnFeel): RingProjection {
  const authored = feel.reach ?? {
    turn: feel.radius,
    accel: feel.radius,
    brake: feel.radius,
  };
  const projected: RingProjected =
    feel.ringMode === 'planing'
      ? applyPlaning(true, speedPx, feel.planingSpeedKmh, authored)
      : feel.ringMode === 'headreach'
        ? headreachFeel(speedPx, authored)
        : {
            level: 0,
            coastPx: Math.max(0, speedPx),
            axes: authored,
          };
  const reach = reachIsDisk(projected.axes) ? null : projected.axes;

  return {
    projected,
    reach,
    diskRadius: reach ? feel.radius : projected.axes.turn,
  };
}

/** On-plane turn bleed. Undefined below the threshold and outside the planing ring. */
export function planingKeptSpeed(
  boat: BoatState,
  point: Vec2,
  feel: TurnFeel,
  projected: RingProjected,
): number | undefined {
  if (feel.ringMode !== 'planing') {
    return undefined;
  }

  const arc = solveTurnArc(boat, point, feel.turnTax);

  if (!arc) {
    return undefined;
  }

  return (
    planingTurnSpeed({
      chord: arc.speed,
      offset: arc.offset,
      speedPx: boat.speed,
      planingSpeedKmh: feel.planingSpeedKmh,
      turnPx: projected.axes.turn,
      coastPx: projected.coastPx,
    }) ?? undefined
  );
}

export function paceFeel(boat: BoatState, feel: TurnFeel): TurnFeel {
  const scale = DRIVE_SCALE[driveLevel(boat)];

  if (scale === 1) {
    return feel;
  }

  return {
    ...feel,
    radius: feel.radius * scale,
    reach: feel.reach
      ? {
          turn: feel.reach.turn * scale,
          accel: feel.reach.accel * scale,
          brake: feel.reach.brake * scale,
        }
      : null,
  };
}

export function seatCommitArgs(boat: BoatState, dest: Vec2, feel: TurnFeel): PlanMoveArgs {
  const paced = paceFeel(boat, feel);
  const view = ringProjection(boat.speed, paced);

  return {
    radius: view.reach ? paced.radius : view.diskRadius,
    crashHarshness: paced.crashHarshness,
    turnTax: paced.turnTax,
    windFeel: paced.windFeel,
    reach: view.reach,
    bowOffsetPx: paced.bowOffsetPx,
    coastPx: view.projected.coastPx,
    keptSpeed: planingKeptSpeed(boat, dest, paced, view.projected),
  };
}

export function planWithFeel(
  boat: BoatState,
  dest: Vec2,
  map: MapDocument,
  feel: TurnFeel,
  /** Other hulls parked where they are now. Omitted, this click has the sea to itself. */
  fleet?: FleetContext,
): CommitResult | null {
  const args = seatCommitArgs(boat, dest, feel);

  return planMove(
    boat,
    dest,
    args.radius,
    map,
    args.crashHarshness,
    args.turnTax,
    args.windFeel,
    args.reach,
    args.bowOffsetPx,
    args.coastPx,
    args.keptSpeed,
    fleet,
  );
}
